import React, { FC, useState, useRef, useEffect } from 'react';
import { Box, LinearProgress, Paper } from '@mui/material';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useTranslation } from 'react-i18next';
import { ActionSet } from 'models/action-set';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import ActionSets from 'components/manage-actions/action-set/action-sets.component';
import MainCategory from 'components/manage-actions/main-category/main-category.component';
import AddActionSetDialog from 'components/manage-actions/action-set/add-action-set-dialog.component';
import SubCategorySection from 'components/manage-actions/sub-category-section/sub-category-section.component';
import MaterialGroupSection from 'components/manage-actions/material-group-section/material-group-section.component';
import ActionSection from 'components/manage-actions/action/action-section.component';

const ManageActions: FC = () => {
    const { t: translate } = useTranslation(['manage-actions']);
    const currentOrg = currentOrgVar();
    const [addActionSetDialogOpen, setAddActionSetDialogOpen] = useState(false);
    const [actionSet, setActionSet] = useState<ActionSet>({} as ActionSet);
    const [loading, setLoading] = useState(false);
    const [selectedActionSetId, setSelectedActionSetId] = useState(currentOrg.activeActionSet || '');
    const [activeActionSet, setActiveActionSet] = useState<string>(currentOrg.activeActionSet || '');
    const actionSetRefetchRef = useRef<((actionSetId: string) => Promise<any>) | null>(null);

    useEffect(() => {
        if (currentOrg?._id && currentOrg.activeActionSet) {
            reFetchActionSet(currentOrg.activeActionSet); // load on first mount
        }
    }, []);

    const handleActionSetChange = (actionSet: ActionSet, loading: boolean) => {
        const sortedActions = [...actionSet.actions].sort((a, b) => parseInt(a.number) - parseInt(b.number));
        const clonedActionSet = {
            ...actionSet,
            actions: actionSet.actions ? sortedActions : [],
            categories: actionSet.categories?.map((category) => ({
                ...category,
                subCategories: category.subCategories?.map((subCategory) => ({
                    ...subCategory,
                    parentCategoryId: category._id as string,
                })),
            })),
        };
        setActionSet(clonedActionSet);
        setLoading(loading);
    };

    const closeAddActionSetDialog = (actionSet?: ActionSet) => {
        setAddActionSetDialogOpen(false);
        if (actionSet) {
            const actionSets = currentOrg.actionSets || [];
            actionSets.push(actionSet);
            currentOrgVar({ ...currentOrg, actionSets });
        }
    };

    const reFetchActionSet = (actionSetId: string) => {
        const refetchFn = actionSetRefetchRef.current;
        const id = actionSetId || selectedActionSetId;
        if (!refetchFn || !id) return Promise.resolve();

        setLoading(true);
        return refetchFn(id).finally(() => setLoading(false));
    };

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <PageTitle headingStyle="h1" title={translate('manage-actions:MANAGE_ACTIONS')} />
            </Box>
            {loading ? (
                <LinearProgress />
            ) : (
                <Paper variant="outlined">
                    <Box p={2}>
                        <Box mb={3}>
                            <ActionSets
                                orgActionSets={currentOrg.actionSets}
                                selectedActionSetId={selectedActionSetId}
                                setSelectedActionSetId={setSelectedActionSetId}
                                activeActionSet={activeActionSet}
                                setActiveActionSet={setActiveActionSet}
                                onActionSetChange={handleActionSetChange}
                                onAddActionSet={() => setAddActionSetDialogOpen(true)}
                                reFetchActionSetRef={actionSetRefetchRef}
                            />
                        </Box>
                        <Box mb={3}>
                            <MainCategory
                                categories={actionSet.categories || []}
                                actionSetId={actionSet._id}
                                reFetchActionSet={reFetchActionSet}
                            />
                        </Box>
                        <Box mb={3}>
                            <SubCategorySection
                                categories={actionSet.categories || []}
                                actionSetId={actionSet._id}
                                reFetchActionSet={reFetchActionSet}
                            />
                        </Box>
                        <Box mb={3}>
                            <MaterialGroupSection
                                materialGroups={actionSet.materialGroups || []}
                                actionSetId={actionSet._id}
                                reFetchActionSet={reFetchActionSet}
                            />
                        </Box>

                        <Box>
                            <ActionSection
                                actionSet={actionSet}
                                actionSetId={actionSet._id}
                                reFetchActionSet={reFetchActionSet}
                            />
                        </Box>
                    </Box>
                </Paper>
            )}
            <AddActionSetDialog open={addActionSetDialogOpen} onClose={closeAddActionSetDialog} />
        </Box>
    );
};
export default ManageActions;
